import type {
  Editor,
  TLDocument,
  TLPageId,
  TLRecord,
  TLScribble,
  TLShapeId,
  TLStoreWithStatus,
  TLInstancePresence,
} from '@digitalsamba/tldraw';
import { selectUserById } from 'features/users/usersSlice';
import { SignalingSocket } from 'services/signaling';
import { store as reduxStore } from 'store/store';
import { lazySingleton } from 'utils/lazySingleton';
import { loadTldraw, getTldrawSync } from 'utils/whiteboard/tldrawRuntime';
import { defaultAssetUrls, TLDrawLanguagesMap } from 'utils/whiteboard/assetUrls';
import {
  CONTENT_BOARD_BG_SHAPE_ID,
  CONTENT_BOARD_COLORS_MAP,
} from 'features/content-whiteboard/constants';
import {
  type initBgImageOptions,
  initializeBackgroundImage,
} from 'features/content-whiteboard/utils/initializeBackgroundImage';
import { initializeBackgroundBounds } from 'features/content-whiteboard/utils/initializeBackgroundBounds';
import type {
  ContentBoardColor,
  SignalingLaserUpdatedPayload,
} from 'features/content-whiteboard/types';
import { getInactiveImageUtil } from 'features/content-whiteboard/components/TldrawEditor/TldrawImageShape';
import { RTCClient } from 'utils/webrtc';
import { UserId } from '../../users/types';

export type RecordId = TLRecord['id'];

export type RecordList = Record<RecordId, TLRecord>;

export interface ContentWhiteboardRemoteUpdateData {
  changed: TLRecord[];
  removed: TLShapeId[];
}

export type ContentBoardData = {
  id: string;
  shapes: TLRecord[];
  type?: ContentBoardType;
  userId?: UserId;
  name?: string;
  page?: number;
};

export type ScreenshareBoardState = {
  shapeId: string;
  data: string;
}[];

export type ContentBoardType = 'file' | 'screenshare';

const CHANGE_INTERVAL = 100;
const LASER_POINTER_CHANGE_INTERVAL = 30;

const TL_DEFAULT_PAGE_ID = 'page:page' as TLPageId;

class ContentBoardStateManager {
  type: ContentBoardType = 'file';

  id: string | null = null;

  currentPage: number | null = null;

  screenshareUserId: string | null = null;

  localScreenshareState: ScreenshareBoardState = [];

  intervalId: number = 0;

  store: TLStoreWithStatus = {
    status: 'loading',
  };

  buffer: RecordList = {};

  removedBuffer: Record<string, boolean> = {};

  initialBuffer: Record<string, boolean> = {};

  laserPointerIntervalId: number = 0;

  laserPointerBuffer: TLScribble[] = [];

  laserPointerChangesPending = false;

  localChangesPending = false;

  editor: Editor | null = null;

  shouldInitStore = false;

  laserTimeouts = new Map<string, NodeJS.Timeout>();

  currentLocale: keyof typeof defaultAssetUrls.translations | null = null;

  initEditor = (editor: Editor) => {
    this.editor = editor;

    this.editor.stopCameraAnimation();

    this.editor.updateInstanceState({
      isDebugMode: false,
      devicePixelRatio: 2,
      canMoveCamera: false,
    });

    if (this.currentLocale) {
      editor.user.updateUserPreferences({
        locale: this.currentLocale,
      });
    }
  };

  initBgImage = (options: Omit<initBgImageOptions, 'editor'>) => {
    if (this.editor) {
      initializeBackgroundImage({
        ...options,
        editor: this.editor,
      });
    }
  };

  initBgBounds = (options: { size: { width: number; height: number } }) => {
    if (this.editor) {
      initializeBackgroundBounds({
        ...options,
        editor: this.editor,
      });
    }
  };

  /**
   * Initialize the content whiteboard.
   * This method is async because it needs to load TLDraw on first use.
   */
  init = async (data: ContentBoardData) => {
    await loadTldraw();

    this.shouldInitStore = true;

    this.createStore();

    this.id = data.id;
    this.currentPage = data.page ?? 0;
    this.type = data.type ?? 'file';
    this.screenshareUserId = data.userId ?? null;

    if (data.shapes.length) {
      this.setState(data.shapes);

      if (RTCClient.localFeeds[this.id]) {
        this.localScreenshareState = data.shapes.map((state) => ({
          shapeId: state.id,
          data: JSON.stringify(state),
        }));
      }
    }
  };

  reset = () => {
    this.store = {
      status: 'loading',
    };

    this.resetData();

    this.stop();
  };

  clean = () => {
    this.cleanStore();
    this.resetData();
  };

  private resetData = () => {
    this.localChangesPending = false;
    this.laserPointerChangesPending = false;
    this.buffer = {};
    this.removedBuffer = {};
    this.initialBuffer = {};
    this.laserPointerBuffer = [];
  };

  createStore = () => {
    if (this.store.status !== 'loading') {
      if (this.shouldInitStore) {
        this.reset();
      } else {
        return;
      }
    }

    this.shouldInitStore = false;

    const tldraw = getTldrawSync();
    const { createTLStore, defaultShapeUtils, DocumentRecordType, PageRecordType } = tldraw;

    const InactiveImageUtil = getInactiveImageUtil();

    const store = createTLStore({ shapeUtils: [...defaultShapeUtils, InactiveImageUtil] });

    store.put([
      DocumentRecordType.create({
        id: 'document:document' as TLDocument['id'],
      }),

      PageRecordType.create({
        id: TL_DEFAULT_PAGE_ID,
        name: 'Page 1',
        index: 'a1',
      }),
    ]);

    store.listen(
      ({ changes }) => {
        Object.values(changes.added).forEach((record: any) => {
          if (this.isBgShape(record)) {
            return;
          }

          this.insertShape(record.id, record);
        });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.values(changes.updated).forEach(([_, record]) => {
          this.insertShape(record.id, record);
        });

        Object.values(changes.removed).forEach((record) => {
          this.removeShape(record.id);
        });
      },
      { source: 'user', scope: 'document' }
    );

    this.store = {
      store,
      status: 'synced-remote',
      connectionStatus: 'online',
    };
  };

  private cleanStore = () => {
    if (!this.store.store) {
      return;
    }

    const shapeIds = this.store.store
      .allRecords()
      .filter((r) => r.typeName === 'shape')
      .map((r) => r.id);

    if (shapeIds.length === 0) {
      return;
    }

    this.store.store.mergeRemoteChanges(() => {
      this.store.store?.remove(shapeIds);
    });
  };

  private isBgShape = (record: any) => {
    if (record.typeName === 'asset' && record?.props.name === CONTENT_BOARD_BG_SHAPE_ID) {
      return true;
    }

    return record.id === CONTENT_BOARD_BG_SHAPE_ID;
  };

  updateLocale = (locale: string) => {
    const mappedLocale = TLDrawLanguagesMap[locale];
    if (!mappedLocale) {
      return;
    }

    this.currentLocale = mappedLocale;

    if (this.editor) {
      this.editor.user.updateUserPreferences({
        locale: mappedLocale,
      });
    }
  };

  insertShape = (id: RecordId, shape: any) => {
    if (!this.initialBuffer[id]) {
      this.buffer[id] = shape;

      delete this.removedBuffer[id];

      this.localChangesPending = true;
    }

    delete this.initialBuffer[id];
  };

  removeShape = (id: RecordId) => {
    delete this.buffer[id];
    delete this.initialBuffer[id];

    this.removedBuffer[id] = true;

    this.localChangesPending = true;
  };

  watch = () => {
    this.intervalId = window.setInterval(this.checkForChanges, CHANGE_INTERVAL);
    this.laserPointerIntervalId = window.setInterval(
      this.updateLaserPointer,
      LASER_POINTER_CHANGE_INTERVAL
    );
  };

  stop = () => {
    clearInterval(this.intervalId);
    clearInterval(this.laserPointerIntervalId);
  };

  setState = (records: TLRecord[]) => {
    this.initialBuffer = records.reduce<Record<string, boolean>>((buffer, record) => {
      buffer[record.id] = true;
      return buffer;
    }, {});

    this.store.store?.put(records);
  };

  acceptRemoteChanges = (data: ContentWhiteboardRemoteUpdateData) => {
    this.store.store?.mergeRemoteChanges(() => {
      const toPut = data.changed;
      const toRemove = data.removed;

      if (toPut.length) {
        this.store.store?.put(toPut);
      }
      if (toRemove.length) {
        this.store.store?.remove(toRemove);
      }
    });
  };

  updateLocalScreenshareState = (data: ContentWhiteboardRemoteUpdateData) => {
    if (data.changed.length === 0 && data.removed.length === 0) {
      return;
    }

    if (data.removed.length) {
      this.localScreenshareState = this.localScreenshareState.filter(
        (local) => !data.removed.includes(local.shapeId as TLShapeId)
      );
    }

    if (data.changed.length) {
      const mainMap = new Map(
        this.localScreenshareState.map((item) => [
          item.shapeId,
          {
            shapeId: item.shapeId,
            data: item.data,
          },
        ])
      );

      data.changed.forEach((item) => {
        mainMap.set(item.id, { shapeId: item.id, data: JSON.stringify(item) });
      });

      this.localScreenshareState = Array.from(mainMap.values());
    }
  };

  cleanAllSelections = () => {
    if (!this.editor) {
      return;
    }

    this.editor.selectNone();
  };

  private flushBuffer = async () => {
    if (!this.localChangesPending) {
      return;
    }

    if (this.id === null || (this.type === 'file' && this.currentPage === null)) {
      return;
    }

    const buffer = { ...this.buffer };
    const removedBuffer = { ...this.removedBuffer };

    this.buffer = {};
    this.removedBuffer = {};
    this.localChangesPending = false;

    const promises = Object.entries(buffer)
      .filter(([, record]) => !this.isBgShape(record))
      .map(async ([recordId, record]) => {
        const serializedData = JSON.stringify(record);

        return {
          shapeId: recordId,
          data: serializedData,
        };
      });

    const changed = await Promise.all(promises);
    const removed = Object.keys(removedBuffer);

    if (changed.length === 0 && removed.length === 0) {
      return;
    }

    if (this.type === 'screenshare') {
      if (RTCClient.localFeeds[this.id]) {
        this.updateLocalScreenshareState({
          changed: changed.map((item) => JSON.parse(item.data)),
          removed: removed.map((item) => item as TLShapeId),
        });
      }

      SignalingSocket.send({
        event: 'updateScreenshareWhiteboard',
        data: {
          feedId: this.id,
          userId: this.screenshareUserId,
          changed,
          removed,
        },
      });

      return;
    }

    SignalingSocket.send({
      event: 'updateContentWhiteboard',
      data: {
        id: this.id,
        page: this.currentPage,
        changed,
        removed,
      },
    });
  };

  private checkForChanges = () => {
    if (this.localChangesPending) {
      this.flushBuffer();
    }
  };

  updateRemoteLaserPointer = (payload: SignalingLaserUpdatedPayload & { data: TLScribble[] }) => {
    if (!this.store.store) {
      return;
    }

    const user = selectUserById(reduxStore.getState(), payload.userId);

    if (!user || !user.name) {
      return;
    }

    if (this.laserTimeouts.has(payload.userId)) {
      clearTimeout(this.laserTimeouts.get(payload.userId));
    }

    const tldraw = getTldrawSync();
    const { InstancePresenceRecordType } = tldraw;

    const presence = InstancePresenceRecordType.create({
      id: InstancePresenceRecordType.createId(payload.userId),
      currentPageId: TL_DEFAULT_PAGE_ID,
      userId: payload.userId,
      userName: user.name,
      color: CONTENT_BOARD_COLORS_MAP[payload.data[0].color as ContentBoardColor],
      lastActivityTimestamp: Date.now(),
      scribbles: payload.data.map((s) => ({ ...s, color: 'laser' })),
      cursor: { x: 0, y: 0, type: 'default', rotation: 0 },
    });

    this.drawRemoteLaserPointer(presence);

    const timeout = setTimeout(() => {
      this.hideRemoteLaserPointer(presence);
      this.laserTimeouts.delete(payload.userId);
    }, 5000);

    this.laserTimeouts.set(payload.userId, timeout);
  };

  hideLocalLaserPointer = () => {
    if (!this.editor) {
      return;
    }

    this.editor.scribbles.reset();
  };

  private hideRemoteLaserPointer = (presence: TLInstancePresence) => {
    const updatedPresence = {
      ...presence,
      scribbles: [],
    };

    this.drawRemoteLaserPointer(updatedPresence);
  };

  private drawRemoteLaserPointer = (presence: TLInstancePresence) => {
    if (!this.store.store) {
      return;
    }

    this.store.store.mergeRemoteChanges(() => {
      this.store.store?.put([presence]);
    });
  };

  onLaserPointerUpdate = (data: any) => {
    const tldraw = getTldrawSync();
    const { TLINSTANCE_ID } = tldraw;

    if (data.source === 'user') {
      if (data.changes.updated[TLINSTANCE_ID]) {
        data.changes.updated[TLINSTANCE_ID].forEach((update: any) => {
          if (update.scribbles?.length > 0) {
            this.updateLaserPointerBuffer(update.scribbles);
          }
        });
      }
    }
  };

  private updateLaserPointerBuffer = (data: TLScribble[]) => {
    const color = this.editor?.instanceState?.stylesForNextShape['tldraw:color'] as any;
    const sharedOpacity = this.editor?.sharedOpacity as any;

    const formattedData = data.map((scribble) => ({
      ...scribble,
      opacity: sharedOpacity?.value || scribble.opacity,
      color: color || scribble.color,
    }));

    this.laserPointerBuffer = this.laserPointerBuffer.concat(formattedData);
    this.laserPointerChangesPending = true;
  };

  private updateLaserPointer = async () => {
    if (this.laserPointerBuffer.length === 0) {
      return;
    }

    this.laserPointerChangesPending = false;

    const laserPointerMap = new Map(this.laserPointerBuffer.map((s) => [s.id, s]));
    const deduplicated = Array.from(laserPointerMap.values());
    const data = JSON.stringify(deduplicated);

    this.laserPointerBuffer = [];

    if (this.type === 'screenshare') {
      SignalingSocket.send({
        event: 'updateScreenshareWhiteboardLaser',
        data: {
          feedId: this.id,
          userId: this.screenshareUserId,
          data,
        },
      });

      return;
    }

    SignalingSocket.send({
      event: 'updateContentWhiteboardLaser',
      data: {
        id: this.id,
        page: this.currentPage,
        data,
      },
    });
  };
}

export const contentBoard = lazySingleton<ContentBoardStateManager>(
  () => new ContentBoardStateManager()
);
